/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton, IconButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { Shimmer, ShimmerElementType } from '@fluentui/react/lib/Shimmer';
import { Stack } from '@fluentui/react/lib/Stack';
import { IToggleProps, IToggleStyles, Toggle } from '@fluentui/react/lib/Toggle';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisDebouncedCollapsableSearchBox } from 'src/core/components/LuisDebouncedCollapsableSearchBox';
import { LuisText } from 'src/core/components/LuisText';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { FileType } from 'src/core/types/FileType';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { commandBarItemSelectionManager } from 'src/core/utils/commandBarItemSelectionManager';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';
import {
    DeletionOperations,
    OptionalColumnKey,
    OptionalColumnVisibility
} from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerQnaPairsUiStore';
import { QnaMakerQnaStore } from 'src/qnaMaker/core/stores/QnaMakerQnaStore';
import { qnaMakerTeachingBubbleMessages } from 'src/qnaMaker/core/utils/qnaMakerTeachingBubbleMessages';

export type QnaMakerQnaCommandBarProps = TableCommandBarProps<QnaMakerQnaStore> & {
    /**
     * A boolean flag indicating if there are unsaved changes.
     */
    isInDirtyState: boolean;

    /**
     * A boolean flag indicating if there are no qnas.
     */
    isInEmptyState: boolean;

    /**
     * QnA pairs count text.
     */
    qnasCountText: string;

    /**
     * Current search query.
     */
    searchQuery: string;

    /**
     * A callback function to change the search query.
     */
    onChangeSearchQuery: (query: string) => void;

    /**
     * A flag indicating whether to enable rich text editor or not.
     */
    enableRichText: boolean;

    /**
     * A callback to toggle enableRichText flag.
     */
    onChangeEnableRichText: () => void;

    /**
     * A flag indicating whether filter is selected or not.
     */
    isFilterSelected: boolean;

    /**
     * A callback to toggle isFilterSelected flag.
     */
    onFilter: () => void;

    /**
     * A callback on save changes click.
     */
    onSave: () => void;

    /**
     * A callback on test button click.
     */
    onTest: () => void;

    /**
     * A callback to delete qnas.
     */
    onDelete: (operation: DeletionOperations) => void;

    /**
     * A boolean flag indicating if there are no qnas being edited.
     */
    isEditQnaPairOn: boolean;

    /**
     * A callback to edit qna.
     */
    onEdit: () => void;

    /**
     * A callback on import button click.
     */
    onImport: (fileFormat: Extract<FileType, 'xlsx' | 'tsv'>) => void;

    /**
     * A callback on export button click.
     */
    onExport: (fileFormat: Extract<FileType, 'xlsx' | 'tsv'>) => void;

    /**
     * A boolean flag indicating if there are no qnas.
     */
    isAddQnaPairOn: boolean;

    /**
     * A callback on add qna button click.
     */
    onAddNewQna: () => void;

    /**
     * Array of optional displayed columns.
     */
    optionalColumnsShown: OptionalColumnVisibility[];

    /**
     * A callback for changing the optional displayed columns.
     */
    onChangeOptionalColumnsShown: (key: OptionalColumnKey) => void;

    /**
     * A boolean flag indicating if there is a save operation in the background
     * to disable all edit actions.
     */
    areEditActionsDisabled: boolean;

    /**
     * Optional flag to display the command bar in loading state.
     */
    isLoading?: boolean;

    /**
     * A boolean flag indicating whether the prompt modal is hidden or not.
     */
    isPromptModalHidden: boolean;
};

const messages = defineMessages({
    searchPlaceholder: {
        id: 'qnaMaker.qnaMakerQnaCommandBar.searchPlaceholder',
        defaultMessage: 'Search pairs'
    },
    enableRichText: {
        id: 'qnaMaker.qnaMakerQnaCommandBar.enableRichText',
        defaultMessage: 'Enable rich text'
    },
    addQuestionPair: {
        id: 'qnaMaker.qnaMakerQnaCommandBar.addQuestionPair',
        defaultMessage: 'Add question pair'
    },
    saveChanges: {
        id: 'qnaMaker.qnaMakerQnaCommandBar.saveChanges',
        defaultMessage: 'Save changes'
    },
    test: {
        id: 'qnaMaker.qnaMakerQnaCommandBar.test',
        defaultMessage: 'Test'
    },
    edit: {
        id: 'qnaMaker.qnaMakerQnaCommandBar.edit',
        defaultMessage: 'Edit'
    },
    delete: {
        id: 'qnaMaker.qnaMakerQnaCommandBar.delete',
        defaultMessage: 'Delete'
    },
    deletePairs: {
        id: 'qnaMaker.qnaMakerQnaCommandBar.deletePairs',
        defaultMessage: 'Delete pair(s)'
    },
    deleteFollowUpPrompts: {
        id: 'qnaMaker.qnaMakerQnaCommandBar.deleteFollowUpPrompts',
        defaultMessage: 'Delete follow up prompts'
    },
    deleteMetadata: {
        id: 'qnaMaker.qnaMakerQnaCommandBar.deleteMetadata',
        defaultMessage: 'Delete metadata'
    },
    show: {
        id: 'qnaMaker.qnaMakerQnaCommandBar.show',
        defaultMessage: 'Show columns'
    },
    filter: {
        id: 'qnaMaker.qnaMakerQnaCommandBar.filter',
        defaultMessage: 'Filter'
    },
    import: {
        id: 'qnaMaker.qnaMakerQnaCommandBar.import',
        defaultMessage: 'Import questions and answers'
    },
    importSubMenuAriaLabel: {
        id: 'qnaMaker.qnaMakerQnaCommandBar.importSubMenuAriaLabel',
        defaultMessage: 'Import questions and answers menu'
    },
    export: {
        id: 'qnaMaker.qnaMakerQnaCommandBar.export',
        defaultMessage: 'Export questions and answers'
    },
    exportSubMenuAriaLabel: {
        id: 'qnaMaker.qnaMakerQnaCommandBar.exportSubMenuAriaLabel',
        defaultMessage: 'Export questions and answers menu'
    },
    exportQnasExcel: {
        id: 'qnaMaker.qnaMakerQnaCommandBar.exportQnasExcel',
        defaultMessage: 'Export as Excel'
    },
    exportQnasTsv: {
        id: 'qnaMaker.qnaMakerQnaCommandBar.exportQnasTsv',
        defaultMessage: 'Export as TSV'
    },
    importQnasExcel: {
        id: 'qnaMaker.qnaMakerQnaCommandBar.importQnasExcel',
        defaultMessage: 'Import as Excel'
    },
    importQnasTsv: {
        id: 'qnaMaker.qnaMakerQnaCommandBar.importQnasTsv',
        defaultMessage: 'Import as TSV'
    },
    context: {
        id: 'qnaMaker.qnaMakerQnaCommandBar.context',
        defaultMessage: 'Context'
    },
    metadata: {
        id: 'qnaMaker.qnaMakerQnaCommandBar.metadata',
        defaultMessage: 'Metadata'
    },
    exportImportItems: {
        id: 'qnaMaker.qnaMakerQnaCommandBar.exportImportItems',
        defaultMessage: 'export and import questions answer pairs menu'
    }
});

const theme = getScopedTheme('QnaMakerQnaCommandBar');

const toggleStyles = classNamesFunction<IToggleProps, IToggleStyles>()({
    root: { marginBottom: '0px' }
});

/**
 * Command bar rendering all available models and filter options
 */
export const QnaMakerQnaCommandBar = observer((props: QnaMakerQnaCommandBarProps) => {
    const {
        onSave,
        onTest,
        onEdit,
        onFilter,
        onDelete,
        onExport,
        onImport,
        qnasCountText,
        searchQuery,
        onAddNewQna,
        selectedItems,
        enableRichText,
        isInDirtyState,
        isAddQnaPairOn,
        isInEmptyState,
        isEditQnaPairOn,
        isFilterSelected,
        isLoading = false,
        isPromptModalHidden,
        onChangeSearchQuery,
        optionalColumnsShown,
        onChangeEnableRichText,
        areEditActionsDisabled,
        onChangeOptionalColumnsShown
    } = props;

    const { localize } = useLocalization();
    const { serviceName } = getPortalFromUrl();
    const { uiStore, rootStore } = useGlobalState();
    const addButtonIdRef = React.useRef<string>(getId());
    const deleteButtonIdRef = React.useRef<string>(getId());
    const saveChangesButtonIdRef = React.useRef<string>(getId());
    const editButtonIdRef = React.useRef<string>(getId());
    const testButtonIdRef = React.useRef<string>(getId());
    const showColumnsButtonIdRef = React.useRef<string>(getId());
    const filterButtonIdRef = React.useRef<string>(getId());
    const importExportButtonIdRef = React.useRef<string>(getId());

    const [isAddButtonDisabled, setIsAddButtonDisabled] = React.useState(true);
    const [isEditButtonDisabled, setIsEditButtonDisabled] = React.useState(true);
    const [isSaveButtonDisabled, setIsSaveButtonDisabled] = React.useState(true);
    const [isDeleteButtonDisabled, setIsDeleteButtonDisabled] = React.useState(true);

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const { id: currentProjectId } = rootStore.currentQnaMakerProjectStore;

    React.useEffect(
        () => () => {
            dequeueTeachingBubble('QnaEdit');
            dequeueTeachingBubble('QnaTest');
            dequeueTeachingBubble('QnaDelete');
            dequeueTeachingBubble('QnaFilter');
            dequeueTeachingBubble('QnaShowColumns');
            dequeueTeachingBubble('QnaSaveChanges');
            dequeueTeachingBubble('QnaAddQuestionPair');
        },
        []
    );

    React.useEffect(() => {
        if (!isPromptModalHidden) {
            dequeueTeachingBubble('QnaEdit');
            return;
        }

        if (!isAddButtonDisabled && !uiStore.teachingBubbles.QnaAddQuestionPair) {
            createTeachingBubble(
                'QnaAddQuestionPair',
                localize(qnaMakerTeachingBubbleMessages.addQuestionPairBody),
                localize(qnaMakerTeachingBubbleMessages.addQuestionPairTitle),
                `#${addButtonIdRef.current}`,
                true,
                true
            );

            return;
        }

        if (isPromptModalHidden && !isEditButtonDisabled && !uiStore.teachingBubbles.QnaEdit && !uiStore.teachingBubbles.SynonymEdit) {
            createTeachingBubble(
                'QnaEdit',
                localize(qnaMakerTeachingBubbleMessages.qnaEditBody),
                localize(qnaMakerTeachingBubbleMessages.qnaEditTitle),
                `#${editButtonIdRef.current}`,
                true,
                true
            );

            return;
        }

        if (
            !isDeleteButtonDisabled &&
            uiStore.teachingBubbles.QnaEdit &&
            !uiStore.teachingBubbles.QnaDelete &&
            !uiStore.teachingBubbles.SynonymDelete &&
            !uiStore.skippedTeachingBubbles.QnaEdit
        ) {
            createTeachingBubble(
                'QnaDelete',
                localize(qnaMakerTeachingBubbleMessages.qnaDeleteBody),
                localize(qnaMakerTeachingBubbleMessages.qnaDeleteTitle),
                `#${deleteButtonIdRef.current}`,
                true
            );

            return;
        }

        if (
            isInDirtyState &&
            !isSaveButtonDisabled &&
            !uiStore.teachingBubbles.QnaSaveChanges &&
            !uiStore.teachingBubbles.SynonymSaveChanges
        ) {
            createTeachingBubble(
                'QnaSaveChanges',
                localize(qnaMakerTeachingBubbleMessages.qnaSaveChangesBody),
                localize(qnaMakerTeachingBubbleMessages.saveChangesTitle),
                `#${saveChangesButtonIdRef.current}`,
                true,
                true
            );

            return;
        }

        if (
            !isInEmptyState &&
            !isInDirtyState &&
            !uiStore.teachingBubbles.QnaTest &&
            uiStore.teachingBubbles.QnaSaveChanges &&
            !uiStore.skippedTeachingBubbles.QnaSaveChanges
        ) {
            createTeachingBubble(
                'QnaTest',
                localize(qnaMakerTeachingBubbleMessages.testBody),
                localize(qnaMakerTeachingBubbleMessages.testTitle),
                `#${testButtonIdRef.current}`,
                true,
                true
            );

            return;
        }

        if (
            !isInEmptyState &&
            uiStore.teachingBubbles.QnaTest &&
            !uiStore.teachingBubbles.QnaShowColumns &&
            !uiStore.skippedTeachingBubbles.QnaTest
        ) {
            createTeachingBubble(
                'QnaShowColumns',
                localize(qnaMakerTeachingBubbleMessages.showColumnsBody),
                localize(qnaMakerTeachingBubbleMessages.showColumnsTitle),
                `#${showColumnsButtonIdRef.current}`,
                true,
                true
            );
        }

        if (
            !isInEmptyState &&
            uiStore.teachingBubbles.QnaShowColumns &&
            !uiStore.teachingBubbles.QnaFilter &&
            !uiStore.skippedTeachingBubbles.QnaShowColumns
        ) {
            createTeachingBubble(
                'QnaFilter',
                localize(qnaMakerTeachingBubbleMessages.filterBody),
                localize(qnaMakerTeachingBubbleMessages.filterTitle),
                `#${filterButtonIdRef.current}`,
                true,
                true
            );
        }
    }, [
        isInDirtyState,
        isInEmptyState,
        isPromptModalHidden,
        isAddButtonDisabled,
        isEditButtonDisabled,
        isSaveButtonDisabled,
        isDeleteButtonDisabled,
        Object.values(uiStore.teachingBubbles)
    ]);

    const deleteMenuItems: IContextualMenuItem[] = [
        {
            key: 'pairs',
            text: localize(messages.deletePairs),
            onClick: () => onDelete('qna')
        },
        {
            key: 'followUpPrompts',
            text: localize(messages.deleteFollowUpPrompts),
            onClick: () => onDelete('followUpPrompts')
        },
        {
            key: 'metadata',
            text: localize(messages.deleteMetadata),
            onClick: () => onDelete('metadata')
        }
    ];

    const getImportMenuItem = () =>
        !canWrite
            ? {
                  key: 'import',
                  onRender: () => (
                      <RBACTooltip
                          hidden={false}
                          displayedUserRole={displayedUserRole}
                          data-automation-id="importQnaButtonLanguageToolTip"
                          componentSurroundedByToolTip={
                              <CommandBarButton
                                  disabled
                                  role="menuitem"
                                  name={localize(messages.import)}
                                  text={localize(messages.import)}
                                  styles={{ root: { height: '40px' } }}
                                  data-automation-id="importQnaButton"
                                  ariaLabel={localize(messages.import)}
                              />
                          }
                      />
                  )
              }
            : {
                  key: 'import',
                  role: 'menuitem',
                  text: localize(messages.import),
                  ariaLabel: localize(messages.import),
                  subMenuProps: {
                      id: 'importSubMenu',
                      ariaLabel: localize(messages.importSubMenuAriaLabel),
                      items: [
                          {
                              key: 'importExcel',
                              role: 'menuitem',
                              name: localize(messages.importQnasExcel),
                              onClick: () => onImport('xlsx')
                          },
                          { key: 'importTsv', role: 'menuitem', name: localize(messages.importQnasTsv), onClick: () => onImport('tsv') }
                      ]
                  }
              };

    const importExportMenuItems: IContextualMenuItem[] = [
        getImportMenuItem(),
        {
            key: 'export',
            role: 'menuitem',
            disabled: isInEmptyState,
            text: localize(messages.export),
            ariaLabel: localize(messages.export),
            subMenuProps: {
                id: 'exportSubMenu',
                ariaLabel: localize(messages.exportSubMenuAriaLabel),
                items: [
                    { key: 'exportExcel', role: 'menuitem', name: localize(messages.exportQnasExcel), onClick: () => onExport('xlsx') },
                    { key: 'exportTsv', role: 'menuitem', name: localize(messages.exportQnasTsv), onClick: () => onExport('tsv') }
                ]
            }
        }
    ];

    const showColumnsMenuItems: IContextualMenuItem[] = [
        {
            key: 'context',
            text: localize(messages.context),
            onClick: _event => {
                _event.preventDefault();
                logButtonClick(serviceName, 'EditKb', 'ShowContextColumns', 'Qnas', currentProjectId);
                onChangeOptionalColumnsShown('context');
            },
            checked: optionalColumnsShown.find(c => c.key === 'context').isVisible,
            canCheck: true
        },
        {
            key: 'metadata',
            text: localize(messages.metadata),
            onClick: _event => {
                _event.preventDefault();
                logButtonClick(serviceName, 'EditKb', 'ShowMetadataColumns', 'Qnas', currentProjectId);
                onChangeOptionalColumnsShown('metadata');
            },
            checked: optionalColumnsShown.find(c => c.key === 'metadata').isVisible,
            canCheck: true
        }
    ];

    const items: ICommandBarItemProps[] = [
        {
            key: 'add',
            onRender: (item: ICommandBarItemProps) => {
                const _disabled = item.disabled || isAddQnaPairOn || areEditActionsDisabled;
                setIsAddButtonDisabled(!canWrite || _disabled);

                return (
                    <RBACTooltip
                        hidden={canWrite || _disabled}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="addQuestionPairButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                onClick={onAddNewQna}
                                id={addButtonIdRef.current}
                                iconProps={{ iconName: 'Add' }}
                                disabled={!canWrite || _disabled}
                                name={localize(messages.addQuestionPair)}
                                text={localize(messages.addQuestionPair)}
                                data-automation-id="addQuestionPairButton"
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            />
                        }
                    />
                );
            }
        },
        {
            key: 'saveChanges',
            onRender: (item: ICommandBarItemProps) => {
                const _disabled = item.disabled || !isInDirtyState || isEditQnaPairOn || isAddQnaPairOn || areEditActionsDisabled;
                setIsSaveButtonDisabled(!canWrite || _disabled);

                return (
                    <RBACTooltip
                        hidden={canWrite || _disabled}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="saveChangesButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                onClick={onSave}
                                iconProps={{ iconName: 'Save' }}
                                disabled={!canWrite || _disabled}
                                id={saveChangesButtonIdRef.current}
                                name={localize(messages.saveChanges)}
                                text={localize(messages.saveChanges)}
                                data-automation-id="saveChangesButton"
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            />
                        }
                    />
                );
            }
        },
        {
            key: 'edit',
            onRender: (item: ICommandBarItemProps) => {
                const _disabled = item.disabled || areEditActionsDisabled;
                setIsEditButtonDisabled(!canWrite || _disabled);

                return (
                    <RBACTooltip
                        hidden={canWrite || _disabled}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="editQuestionPairButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                onClick={onEdit}
                                id={editButtonIdRef.current}
                                name={localize(messages.edit)}
                                text={localize(messages.edit)}
                                disabled={!canWrite || _disabled}
                                iconProps={{ iconName: 'Edit' }}
                                data-automation-id="editQuestionPairButton"
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            />
                        }
                    />
                );
            }
        },
        {
            key: 'test',
            name: 'testButton',
            id: testButtonIdRef.current,
            text: localize(messages.test),
            iconProps: { iconName: 'TestBeaker' },
            onClick: onTest
        },
        {
            key: 'delete',
            onRender: (item: ICommandBarItemProps) => {
                const _disabled = item.disabled || areEditActionsDisabled;
                setIsDeleteButtonDisabled(!canWrite || _disabled);

                return (
                    <RBACTooltip
                        hidden={canWrite || _disabled}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="deleteQuestionPairButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                id={deleteButtonIdRef.current}
                                name={localize(messages.delete)}
                                text={localize(messages.delete)}
                                disabled={!canWrite || _disabled}
                                iconProps={{ iconName: 'Delete' }}
                                menuProps={{ items: deleteMenuItems }}
                                data-automation-id="deleteQuestionPairButton"
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            />
                        }
                    />
                );
            }
        },
        {
            key: 'showColumns',
            id: showColumnsButtonIdRef.current,
            text: localize(messages.show),
            iconProps: { iconName: 'Column' },
            subMenuProps: { items: showColumnsMenuItems }
        },
        {
            key: 'enableRichText',
            onRender: () => (
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8, padding: '8px' }}>
                    <LuisText size="14px" color={isLoading && theme.disabledTextColor}>
                        {localize(messages.enableRichText)}
                    </LuisText>

                    <Toggle
                        styles={toggleStyles}
                        onChange={onChangeEnableRichText}
                        checked={enableRichText}
                        ariaLabel={localize(messages.enableRichText)}
                        disabled={isLoading}
                    />
                </Stack>
            )
        },
        {
            key: 'importAndExport',
            onRender: () => (
                <IconButton
                    iconProps={{ iconName: 'More' }}
                    disabled={areEditActionsDisabled}
                    id={importExportButtonIdRef.current}
                    style={{ padding: '8px', height: '100%' }}
                    menuIconProps={{ style: { display: 'none' } }}
                    ariaLabel={localize(messages.exportImportItems)}
                    menuProps={{
                        id: 'importExportMenuItems',
                        items: importExportMenuItems,
                        ariaLabel: localize(messages.exportImportItems)
                    }}
                />
            )
        }
    ];

    const farItems = [
        {
            key: 'itemsCount',
            onRender: () =>
                isLoading ? (
                    <Shimmer
                        shimmerElements={[{ type: ShimmerElementType.line, width: '70px', height: 15 }]}
                        styles={{ root: { marginTop: '15px', marginRight: '10px' } }}
                    />
                ) : (
                    <LuisText styles={{ root: { display: 'flex', alignItems: 'center', marginRight: '5px' } }}>{qnasCountText}</LuisText>
                )
        },
        {
            key: 'search',
            onRender: () => (
                <LuisDebouncedCollapsableSearchBox
                    ariaLabel={localize(messages.searchPlaceholder)}
                    size={24}
                    value={searchQuery}
                    placeholder={localize(messages.searchPlaceholder)}
                    onChange={(_, newValue?: string) => onChangeSearchQuery(newValue)}
                />
            )
        },
        {
            key: 'filter',
            id: filterButtonIdRef.current,
            onClick: onFilter,
            text: localize(messages.filter),
            iconProps: { iconName: isFilterSelected ? 'FilterSolid' : 'Filter' }
        }
    ];

    const updatedItems: ICommandBarItemProps[] = commandBarItemSelectionManager(
        items,
        ['add', 'saveChanges', 'test', 'showColumns', 'importAndExport', 'enableRichText'],
        ['add', 'saveChanges', 'edit', 'delete', 'test', 'showColumns', 'enableRichText'],
        ['add', 'saveChanges', 'edit', 'delete', 'showColumns', 'test', 'enableRichText'],
        selectedItems.length
    );

    return <LuisCommandBar items={updatedItems} farItems={farItems} showTopBorder={false} />;
});
